import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Download, FileAudio, Loader2, Package, X } from "lucide-react";
import useEscapeClose from "@/lib/useEscapeClose";
import { downloadBlob, downloadUrl, playableUrl, safeFilename } from "@/lib/creatorApi";

const sampleRateLabel = (engine) => `${((engine?.ctx?.sampleRate || 44100) / 1000).toFixed(1).replace(/\.0$/, "")} kHz`;
const OVERLAY_FADE = { opacity: 0 };
const OVERLAY_FULL = { opacity: 1 };
const PANEL_INIT = { opacity: 0, y: 24 };
const PANEL_IN = { opacity: 1, y: 0 };

/** Export Song — MP3 as generated, or a studio WAV rendered from the engine (includes mastering). */
export default function ExportSheet({ open, onClose, song, engine, hasMaster = false }) {
  useEscapeClose(open, onClose);
  const [busy, setBusy] = useState(null);

  const exportMp3 = async () => {
    setBusy("mp3");
    try {
      await downloadUrl(playableUrl(song.audio_url), safeFilename(song.title, "mp3"));
      toast.success("Downloading your song", { description: "MP3 · as generated" });
    } catch (e) {
      toast.error("Download didn't start", { description: e?.message || "Try again in a moment." });
    } finally { setBusy(null); }
  };

  const exportWav = async (bitDepth) => {
    if (!engine || engine.maxDuration <= 0) { toast.error("Nothing to export yet", { description: "Wait for the song to finish loading." }); return; }
    setBusy(`wav${bitDepth}`);
    try {
      const blob = await engine.exportWav({ bitDepth });
      downloadBlob(blob, safeFilename(`${song.title}${hasMaster ? " - mastered" : ""}`, "wav"));
      toast.success("Audio file created", { description: `WAV · ${bitDepth}-bit · ${sampleRateLabel(engine)} · ${(blob.size / 1024 / 1024).toFixed(1)} MB` });
    } catch (e) {
      toast.error("Export failed", { description: e?.message || "Try again." });
    } finally { setBusy(null); }
  };

  const Option = ({ id, icon: Icon, title, body, onClick, primary }) => (
    <button
      type="button"
      data-testid={`export-option-${id}`}
      disabled={!!busy}
      onClick={onClick}
      className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors duration-200 disabled:opacity-60 ${
        primary ? "border-transparent bg-gradient-to-r from-blue-500/30 to-violet-600/30 hover:from-blue-500/40 hover:to-violet-600/40" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.08]"
      }`}
    >
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/[0.08]">
        {busy === id ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
      </span>
      <span className="min-w-0">
        <span className="block font-display text-sm font-semibold">{title}</span>
        <span className="block text-[12px] text-white/55">{body}</span>
      </span>
    </button>
  );

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="export-sheet"
          initial={OVERLAY_FADE} animate={OVERLAY_FULL} exit={OVERLAY_FADE}
          className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4 backdrop-blur-md"
          onClick={busy ? undefined : onClose}
          data-testid="export-sheet"
        >
          <motion.div
            initial={PANEL_INIT} animate={PANEL_IN} exit={PANEL_INIT}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0b0e1c] p-6 text-white shadow-2xl"
          >
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="font-display text-xl font-bold">Export Song</h2>
                <p className="mt-1 text-sm text-white/55">Choose the file you want to download.</p>
              </div>
              <button type="button" data-testid="export-close" onClick={onClose} disabled={!!busy} className="rounded-xl border border-white/10 bg-white/[0.06] p-2 text-white/70 hover:bg-white/[0.12]"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <Option id="wav24" icon={FileAudio} primary title={hasMaster ? "Mastered WAV · 24-bit" : "Studio WAV · 24-bit"} body={hasMaster ? "Your mastered song, ready for release." : `Highest quality · ${sampleRateLabel(engine)} · includes everything loaded in the studio.`} onClick={() => exportWav(24)} />
              <Option id="wav16" icon={FileAudio} title="WAV · 16-bit" body="CD quality · smaller file." onClick={() => exportWav(16)} />
              <Option id="mp3" icon={Download} title="MP3 · as generated" body="The original file — quick to share." onClick={exportMp3} />
              <Link to={`/create/export-audio?song=${song.id}`} data-testid="export-option-pack" className="flex w-full items-center gap-4 rounded-2xl border border-sky-300/25 bg-sky-400/[0.06] p-4 text-left transition-colors duration-200 hover:bg-sky-400/[0.12]">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/[0.08]"><Package className="h-5 w-5" /></span>
                <span className="min-w-0">
                  <span className="block font-display text-sm font-semibold">Studio One pack · 24-bit 48 kHz</span>
                  <span className="block text-[12px] text-white/55">Full mix + stems, every file lined up at 0:00, in one zip.</span>
                </span>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
